import { EquipmentViewFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { SelectedEquipmentPanel } from "#src/batteries-included-components/Panels/SelectedEntitiesPanels/SelectedEquipmentPanel";
import { EquipmentTablePanel } from "#src/batteries-included-components/Panels/TablePanels/EquipmentTablePanel";
import { useClearEquipmentCache } from "#src/components/hooks/adapters/useEquipment";
import {
  EventCategoryContext,
  EventCategoryProvider,
} from "#src/contexts/EventCategoryContext";
import { EventContext, EventProvider } from "#src/contexts/EventContext";
import {
  EventTemplateContext,
  EventTemplateProvider,
} from "#src/contexts/EventTemplateContext";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { getBreadcrumbsObject } from "#src/Routers/breadcrumbsHelper";
import { useHistory, useNavigate, useParams } from "#src/Routers/hooks";
import { EVENTS_BREADCRUMB } from "#src/routes/events";
import { EVENT_CATEGORIES_BREADCRUMB } from "#src/routes/events/event-categories";
import { EVENT_CATEGORY_DETAIL_BREADCRUMB } from "#src/routes/events/event-categories/[eventCategoryId]";
import { EVENT_TEMPLATE_DETAIL_BREADCRUMB } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/details";
import {
  EVENT_DETAIL_BREADCRUMB,
  linkToEventDetails,
} from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/events/[eventId]";
import { Button, Page } from "@validereinc/common-components";
import { EquipmentType, EventAdapter } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useContext, useState } from "react";
import { ADD_EQUIPMENT_BREADCRUMB, ADD_EQUIPMENT_PAGE_TITLE } from "../";
import styles from "./AddEquipmentPage.module.scss";

const cx = classNames.bind(styles);

export const AddEquipmentToEventContent = () => {
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    "event-add-equipment"
  );
  const urlParams = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const history = useHistory();
  const { invalidate: invalidateEquipmentCache } = useClearEquipmentCache();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { eventCategory } = useContext(EventCategoryContext) || {};
  const { eventTemplate } = useContext(EventTemplateContext) || {};
  const { event } = useContext(EventContext) || {};

  const [selectedEquipment, setSelectedEquipment] = useState<
    Record<string, EquipmentType>
  >({});

  const breadcrumbs = getBreadcrumbsObject(
    [
      EVENTS_BREADCRUMB,
      EVENT_CATEGORIES_BREADCRUMB,
      EVENT_CATEGORY_DETAIL_BREADCRUMB,
      EVENT_TEMPLATE_DETAIL_BREADCRUMB,
      EVENT_DETAIL_BREADCRUMB,
      ADD_EQUIPMENT_BREADCRUMB,
    ],
    urlParams,
    { 2: eventCategory?.name, 3: eventTemplate?.name, 4: event?.name }
  );

  const onCancel = async () => {
    history.goBack();
  };

  const onSubmit = async () => {
    setIsSubmitting(true);

    await Promise.all(
      Object.values(selectedEquipment).map(({ id }) => {
        return EventAdapter.assets.createOne({
          eventId: event?.id ?? "",
          assetId: id,
        });
      })
    );

    invalidateEquipmentCache();

    navigate({
      pathname: linkToEventDetails(
        eventCategory?.id,
        eventTemplate?.id,
        event?.id
      ),
    });

    setIsSubmitting(false);
  };

  return (
    <Page
      title={ADD_EQUIPMENT_PAGE_TITLE}
      breadcrumbs={breadcrumbs}
      footer={
        <div className={cx("footer")}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <div style={{ display: "flex", flexDirection: "row", gap: 16 }}>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={
                isSubmitting || !Object.values(selectedEquipment).length
              }
            >
              Add
            </Button>
          </div>
        </div>
      }
    >
      <EquipmentViewFilterArea viewConfigStorageKey={viewConfigStorageKey} />

      <div className={cx("container")}>
        <EquipmentTablePanel
          isWithCustomAttributes={false}
          selected={selectedEquipment}
          onSelectionChange={setSelectedEquipment}
          filterConfigStorageKey={filterConfigStorageKey}
          tableConfigStorageKey={tableConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
          groupByTablesStateStorageKey={groupByTablesStateStorageKey}
        />

        <SelectedEquipmentPanel
          selectedEquipment={selectedEquipment}
          setSelectedEquipment={setSelectedEquipment}
        />
      </div>
    </Page>
  );
};

export const AddEquipmentToEventPage = () => {
  const { eventCategoryId, eventTemplateId, eventId } = useParams<{
    eventCategoryId: string;
    eventTemplateId: string;
    eventId: string;
  }>();

  return (
    <EventCategoryProvider eventCategoryId={eventCategoryId}>
      <EventTemplateProvider eventTemplateId={eventTemplateId}>
        <EventProvider eventId={eventId}>
          <AddEquipmentToEventContent />
        </EventProvider>
      </EventTemplateProvider>
    </EventCategoryProvider>
  );
};
